import React, { useCallback, useRef, useState } from 'react';
import { addDays, format } from 'date-fns';
import styled from 'styled-components';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import dayGridPlugin from '@fullcalendar/daygrid';
import AreaHeaderContent from './calendar/AreaHeaderContent';
import { ResourcePayload } from '../lib/ResourcesPayload';
import { EventsPayload } from '../lib/EventsPayload';
import { EventInfo } from '../lib/EventInfo';
import EventContent from './calendar/EventContent';
import SlotLabelContent from './calendar/SlotLabelContent';
import { OccPayload } from '../lib/OccPayload';
import MoveReservationContent from './calendar/MoveReservationContent';
import { toast } from 'sonner';
import { useUpdateRoom } from '../../../services/booking/hooks/useUpdateRoom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../auth/context/AuthProvider';
import { usePropertyReadOnly } from '../../../shared/hooks/usePropertyReadOnly';
import { OccupancyMonthResponse } from '../../../services/occupancy/response/OccupancyMonthResponse';
import { HousekeepingResponseSchemaType } from '../../../services/housekeeping/response/IndexHousekeepingResponse';
import clsx from 'clsx';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const CalendarContainer = styled.div`
  table {
    margin-top: 0px;
  }
  .fc {
    background-color: #ffffff;
  }
  .fc-event {
    background-color: transparent;
    border-width: 0;
    padding: 0;
  }
  .fc-event-title {
    position: relative;
    z-index: 1;
  }
  .fc-timeline-event-harness {
    top: 0px !important;
  }
  th[colspan='1'].fc-day-today {
    // background-color: rgba(7, 106, 78, 0.25);
    background-color: color-mix(in srgb, var(--tblr-primary) 25%, transparent);
    color: white;
  }
  td.fc-day-today {
    // background-color: rgba(7, 106, 78, 0.25);
    background-color: color-mix(in srgb, var(--tblr-primary) 25%, transparent);
    color: white;
  }
  th[colspan='1'].fc-day-future,
  th[colspan='1'].fc-day-past {
    background-color: transparent;
  }
  span.fc-datagrid-expander.fc-datagrid-expander-placeholder,
  span.fc-icon,
  span.fc-datagrid-expander {
    display: none;
  }
  div.fc-datagrid-cell-frame {
    justify-content: center !important;
  }
  .fc-timeline-events {
    padding-top: 0px !important;
    padding-bottom: 0px !important;
    height: 36px !important;
  }
  .fc-timeline-event{
    align-items: normal;
  }
  .fc-timeline-lane-frame {
    height: 36px !important;
  }
  
  /* Height untuk room type */
  tr.fc-resource-roomtype .fc-timeline-lane-frame {
    height: 24px !important;
  }
  tr.fc-resource-roomtype .fc-timeline-events {
    height: 24px !important;
  }
  tr.fc-resource-roomtype td.fc-datagrid-cell {
    height: 24px !important;
  }
  tr.fc-resource-roomtype div.fc-datagrid-cell-frame {
    height: 24px !important;
  }
  tr.fc-resource-roomtype div.fc-datagrid-cell-cushion {
    height: 24px !important;
    padding-top: 2px !important;
    padding-bottom: 2px !important;
  }
  
  /* Height untuk availability event */
  tr.fc-timeline-lane-availability .fc-timeline-lane-frame {
    height: 24px !important;
  }
  tr.fc-timeline-lane-availability .fc-timeline-events {
    height: 24px !important;
  }
  
  .fc-header-toolbar {
    display: none;
  }
  .fc-timeline-slot-cushion:hover {
    text-decoration: none;
  }
  .fc-resource-timeline-divider {
    width: 0px !important;
  }
  .fc-timeline-slot-frame:not(:has(.fc-sticky)) {
    height: 43px !important;
  }
  .fc-timeline-slot-frame:has(.fc-sticky) {
    justify-content: start;
  }
  .fc-timeline-event:not(.fc-event-end)::after, .fc-timeline-event:not(.fc-event-start)::before {
    margin-top: 11px;
  }
`;

interface ResourceLabelInfo {
  resource: {
    title: string;
    extendedProps: {
      type?: string;
      /** Status kamar dari API ('available' | 'closed' | ...), atau 'unsigment' untuk baris Unassigned. */
      status?: string;
      is_housekeeping_enabled?: boolean;
      housekeeping: {
        room_condition: string;
      };
    };
  };
}

const renderResourceLabel = (info: ResourceLabelInfo) => {
  const renderTooltip = (props: React.ComponentPropsWithoutRef<typeof Tooltip>): JSX.Element => (
    <>
      {info.resource.extendedProps.housekeeping && info.resource.extendedProps.housekeeping.room_condition && (
        <Tooltip id="button-tooltip" {...props}>
          {info.resource.extendedProps.housekeeping.room_condition?.charAt(0).toUpperCase() +
            info.resource.extendedProps.housekeeping.room_condition?.slice(1)}
        </Tooltip>
      )}
    </>
  );
  // Kamar tutup hanya tampil kalau toggle "Show closed rooms" aktif — diberi
  // penanda supaya tidak dikira kamar yang masih bisa dijual.
  const isClosedRoom = info.resource.extendedProps.status === 'closed';

  return (
    <div className='d-flex justify-content-between align-items-center resource-label'>
      <span className={`resource-label-text calendar-${info.resource.title.toLocaleLowerCase()}`}>
        {info.resource.title}
      </span>
      {isClosedRoom && (
        <span
          className="badge text-white ms-1"
          style={{ backgroundColor: 'var(--tblr-secondary)', fontSize: '9px' }}
          title="This room is closed and cannot be sold. Move any remaining booking to another room."
        >
          Closed
        </span>
      )}
      {info.resource.extendedProps.housekeeping && info.resource.extendedProps.housekeeping.room_condition && (
        <OverlayTrigger
          placement="right"
          delay={{ show: 250, hide: 400 }}
          overlay={renderTooltip}
        >
          <span className={clsx("badge resource-label-badge", info.resource.extendedProps.housekeeping.room_condition === 'dirty' ? 'bg-danger' : 'bg-success')}></span>
        </OverlayTrigger>
      )
      }
    </div>
  )
};

interface CalendarBookingProps {
  startDate: string;
  endDate: string;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  setModalFilterOpen: (isOpen: boolean) => void;
  resourcesPayload: ResourcePayload[];
  eventsPayload: EventsPayload[];
  occupancy: OccPayload[];
  onUpdate: (id: string) => void;
  onFixIt: (id: string) => void;
  onComment: (id: string) => void;
  onUpdateStatus: (id: string) => void;
  onAddPayment: (id: string) => void;
  onAddExtra: (id: string) => void;
  onEditRoomMaintenance: (id: string) => void;
  onMultipleDate: (startDate: string, endDate: string, roomTYpeId: string, roomId: string) => void;
  occMonth: OccupancyMonthResponse | undefined;
  housekeeping?: HousekeepingResponseSchemaType;
}

interface MoveEventInfo {
  bookingId: string;
  newRoomId: string | undefined;
  info: any;
}

const CalendarBooking: React.FC<CalendarBookingProps> = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  setModalFilterOpen,
  resourcesPayload,
  eventsPayload,
  occupancy,
  onUpdate,
  onFixIt,
  onComment,
  onUpdateStatus,
  onAddPayment,
  onAddExtra,
  onEditRoomMaintenance,
  onMultipleDate,
  occMonth
}) => {
  const { user } = useAuth();
  const { isReadOnly } = usePropertyReadOnly();
  const target = useRef(null);
  const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null);
  const [showMoveEventModal, setShowMoveEventModal] = useState<boolean>(false);
  const [moveEventModalBookingId, setMoveEventModalBookingId] = useState<string | null>(null);
  const [moveEventModalRoomId, setMoveEventModalRoomId] = useState<string | null>(null);
  const [moveEventModalRoomTypeId, setMoveEventModalRoomTypeId] = useState<string | null>(null);
  const [currentMoveEvent, setCurrentMoveEvent] = useState<any | null>(null);

  const handleOpenMoveEventModal = (bookingId: string, roomId: string, roomtype: string, event: any) => {
    if (isReadOnly) {
      event?.revert();
      return;
    }
    setShowMoveEventModal(true);
    setMoveEventModalBookingId(bookingId);
    setMoveEventModalRoomId(roomId);
    setMoveEventModalRoomTypeId(roomtype);
    setCurrentMoveEvent(event);
  };

  const handleCloseMoveEventModal = () => {
    setShowMoveEventModal(false);
    setMoveEventModalBookingId(null);
    setMoveEventModalRoomId(null);
    setCurrentMoveEvent(null);
  };

  const handleCancelMoveEvent = () => {
    if (currentMoveEvent) {
      currentMoveEvent.revert();
      setCurrentMoveEvent(null);
    }
    setShowMoveEventModal(false);
    setMoveEventModalBookingId(null);
    setMoveEventModalRoomId(null);
  };

  const { mutate: moveEvent } = useUpdateRoom();

  const queryClient = useQueryClient();

  const handleMoveEvent = useCallback(({ bookingId, newRoomId, info }: MoveEventInfo) => {
    if (isReadOnly) {
      info.revert();
      return;
    }

    toast.loading('Booking Moving...', { id: 'event-moved' });

    let newRoomIdPayload = newRoomId;

    if (!newRoomIdPayload) {
      toast.error('New room ID is required', { id: 'event-moved' });
      return;
    }

    if (newRoomId && newRoomId.includes('unsigment')) {
      newRoomIdPayload = '';
    }


    moveEvent({ bookingId: bookingId, formData: { room_id: newRoomIdPayload } }, {
      onSuccess: async () => {
        await queryClient.refetchQueries({
          predicate: (query) => query.queryKey.some((key) => ['bookings'].includes(key as string))
        });

        toast.success('Booking moved successfully', { id: 'event-moved' });
      },
      onError: (mutationError) => {
        if ((mutationError as any)?.status === 422) {
          const validationErrors = (mutationError as any)?.response?.data?.errors;
          if (validationErrors) {
            for (const [field, messages] of Object.entries(validationErrors)) {
              toast.error(`${field}: ${(messages as string[]).join(', ')}`, { id: 'event-moved' });
            }
          }
        }
        info.revert();
      },
    });
  }, [moveEvent, isReadOnly]);

  return (
    <CalendarContainer>
      <FullCalendar
        ref={target}
        plugins={[resourceTimelinePlugin, dayGridPlugin, timeGridPlugin, interactionPlugin]}
        headerToolbar={{
          left: '',
          center: '',
          right: '',
        }}
        visibleRange={{
          start: startDate,
          // FullCalendar visibleRange.end bersifat exclusive — tambah 1 hari di sini saja
          // (khusus untuk kalender), supaya endDate tetap merepresentasikan hari terakhir
          // yang sebenarnya untuk keperluan lain (trigger Jump to Date, query data, dst).
          end: format(addDays(new Date(endDate), 1), 'yyyy-MM-dd'),
        }}
        height="auto"
        initialView="resourceTimeline"
        editable={false}
        // Property tidak aktif => kalender jadi view-only: drag event ke kamar
        // lain dimatikan di level FullCalendar (bukan cuma ditolak di handler),
        // supaya event-nya memang tidak bisa diangkat sama sekali.
        eventResourceEditable={!isReadOnly}
        resourceAreaHeaderContent={
          <AreaHeaderContent
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            setModalFilterOpen={setModalFilterOpen}
          />
        }
        resources={resourcesPayload}
        resourceLabelDidMount={(info) => {
          const type = info.resource.extendedProps.type;
          let backgroundColor = '#ffffff';


          if (type === 'roomType') {
            // backgroundColor = '#076a4e40';
            backgroundColor = 'color-mix(in srgb, var(--tblr-primary) 25%, transparent)';
            // Add class to the row for CSS targeting
            const row = info.el.closest('tr');
            if (row) {
              row.classList.add('fc-resource-roomtype');
            }
          }

          info.el.style.backgroundColor = backgroundColor;
        }}
        resourceLaneDidMount={(info) => {
          const type = info.resource.extendedProps.type;

          if (type === 'roomType') {
            // Add class to the lane/row
            const laneEl = info.el;
            const row = laneEl.closest('tr');
            if (row) {
              row.classList.add('fc-resource-roomtype');
            }

            // Set height directly on lane element itself
            laneEl.style.setProperty('height', '24px', 'important');

            // Set height on all relevant child elements
            const laneFrame = laneEl.querySelector('.fc-timeline-lane-frame');
            const eventsContainer = laneEl.querySelector('.fc-timeline-events');
            const laneBg = laneEl.querySelector('.fc-timeline-bg');

            if (laneFrame) {
              (laneFrame as HTMLElement).style.setProperty('height', '24px', 'important');
            }
            if (eventsContainer) {
              (eventsContainer as HTMLElement).style.setProperty('height', '24px', 'important');
            }
            if (laneBg) {
              (laneBg as HTMLElement).style.setProperty('height', '24px', 'important');
            }

            // Also set on all td cells in the row
            if (row) {
              const tds = row.querySelectorAll('td');
              tds.forEach((td) => {
                (td as HTMLElement).style.setProperty('height', '24px', 'important');
              });
            }
          }
        }}
        events={eventsPayload}
        eventDidMount={(info) => {
          if (info.el) {
            const eventTd = info.el.closest("td");

            if (eventTd) {
              if (info.event.extendedProps.type === 'availability') {
                // eventTd.style.backgroundColor = "#076a4e40";
                eventTd.style.backgroundColor = 'color-mix(in srgb, var(--tblr-primary) 25%, transparent)';
                // Add class to the row for CSS targeting
                const row = eventTd.closest('tr');
                if (row) {
                  row.classList.add('fc-timeline-lane-availability');
                }
              }
            }
          }
        }}
        showNonCurrentDates
        selectable={!isReadOnly}
        select={(info) => {
          // Drag-create booking baru di ruang kosong kalender -- dimatikan
          // selama property aktif berstatus nonaktif (read-only mode).
          if (isReadOnly) return;

          const authorized = user?.role.includes('dev') || user?.permissions.includes('booking_create');

          if (!authorized) {
            return;
          }

          const status = info.resource?.extendedProps.status;

          if (status === 'unsigment') return;
          // Baris header "Closed Room" murni label pengelompokan, bukan kamar
          // sungguhan — tidak boleh jadi tempat drag-create booking baru.
          if (status === 'closed-group') return;
          // Baris "Needs Attention" bukan room type sungguhan — tidak ada
          // room_type_id untuk dijadikan booking baru di sini.
          if (info.resource?.id === 'needs-attention') return;
          // Kamar tutup tidak bisa dijual — cegah drag-create booking baru di sini.
          // Baris kamar closed memang sengaja ditampilkan (toggle "Show closed
          // rooms") HANYA untuk memindahkan booking yang masih tersangkut, bukan
          // untuk menerima booking baru.
          if (status === 'closed') {
            toast.error('This room is closed and cannot be sold. Please pick another room.', { id: 'closed-room-create' });
            return;
          }

          const isAllDay = info.allDay;
          const adjustedEndStr = isAllDay
            ? new Date(new Date(info.endStr).getTime() - 1).toISOString().split("T")[0]
            : info.endStr;

          onMultipleDate(info.startStr, adjustedEndStr, info.resource?.extendedProps?.room_type_id || '', info.resource?.id || '');
        }}
        slotLabelFormat={[
          { month: 'long', year: 'numeric' },
          { day: 'numeric', weekday: 'short' },
        ]}
        slotLabelContent={(info: { text: string, level: number }) => <SlotLabelContent info={info} occupancy={occupancy} occMonth={occMonth} />}
        slotLabelClassNames="fc-slot-label-custom"
        eventContent={(eventInfo: EventInfo) => <EventContent eventInfo={eventInfo} startDate={startDate} endDate={endDate} activeTooltipId={activeTooltipId} setActiveTooltipId={setActiveTooltipId} onUpdate={onUpdate} onFixIt={onFixIt} onComment={onComment} onUpdateStatus={onUpdateStatus} onAddPayment={onAddPayment} onAddExtra={onAddExtra} onEditRoomMaintenance={onEditRoomMaintenance} />}
        scrollTimeReset={false}
        eventAllow={(dropInfo, draggedEvent) => {
          if (isReadOnly) return false;

          const authorized = user?.role.includes('dev') || user?.permissions.includes('calendar_move_booking');

          if (authorized) {
            if (draggedEvent?.extendedProps.type === 'availability') {
              return false;
            } else if (draggedEvent?.extendedProps.type === 'roomMaintenance') {
              return false;
            }
            // Tolak drop KE kamar tutup. Backend memang sudah menolak
            // (updateBookingRoom memvalidasi kamar tujuan `status = available`),
            // tapi tanpa guard ini user baru tahu setelah request gagal 422.
            // Memindahkan booking KELUAR dari kamar tutup tetap boleh — itu
            // justru tujuan toggle "Show closed rooms".
            const targetStatus = (dropInfo as { resource?: { extendedProps?: { status?: string } } })?.resource?.extendedProps?.status;
            if (targetStatus === 'closed') {
              return false;
            }
            // Baris header "Closed Room" bukan kamar sungguhan -- tolak juga
            // sebagai drop target (beda dari "Unassigned" yang memang valid
            // jadi tempat drop untuk aksi unassign).
            if (targetStatus === 'closed-group') {
              return false;
            }
            // Baris "Needs Attention" juga bukan kamar sungguhan -- tolak
            // sebagai drop TARGET (tidak ada resource room sungguhan untuk
            // ditempeli). Event booking DI baris ini sendiri tetap bisa
            // di-drag KELUAR ke kamar lain (lihat mapBookingEventCalendar.ts)
            // -- itu memicu modal Move Reservation yang sudah disesuaikan
            // untuk kasus unmapped (wajib pilih rate plan baru sebelum submit).
            if ((dropInfo as { resource?: { id?: string } })?.resource?.id === 'needs-attention') {
              return false;
            }
            return true;
          } else {
            return false;
          }
        }}
        eventDrop={(info) => {
          if (isReadOnly) {
            info.revert();
            return;
          }

          if (info.newResource?._resource?.parentId === '' || info.newResource?._resource?.parentId === null) {
            info.revert();
            return;
          }

          const { oldResource, newResource } = info;
          const oldRoomTypeId = oldResource?.extendedProps?.room_type_id;
          const newRoomTypeId = newResource?.extendedProps?.room_type_id;

          if (oldRoomTypeId !== newRoomTypeId) {
            // Baris "Unassigned" per room type dipakai resource id `unsigment-<room_type_id>`
            // (lihat mapBookingEventCalendar.ts) -- bukan room id sungguhan, jadi
            // dinormalisasi ke '' sebelum diteruskan ke modal Move Reservation.
            const isUnassignedTarget = (newResource?.id || '').startsWith('unsigment-');
            const normalizedRoomId = isUnassignedTarget ? '' : (newResource?.id || '');
            handleOpenMoveEventModal(info.event.id, normalizedRoomId, newRoomTypeId, info);
            return;
          }
          handleMoveEvent({ bookingId: info.event.id, newRoomId: newResource?.id, info });
        }}
        schedulerLicenseKey="0310067844-fcs-1721029428"
        resourceLabelContent={renderResourceLabel}
        resourceOrder={'order'}
        resourceLabelClassNames={(info) => {
          const type = info.resource.title;

          return `resource-label calendar-${type.toLocaleLowerCase()}`;
        }}
        dayCellClassNames={(info) => {
          console.log('info', info);
          return '';
        }}
        resourceLaneClassNames={(info) => {
          const type = info.resource.title;

          return `resource-lane calendar-${type.toLocaleLowerCase()}`;
        }}
        resourceAreaWidth="20%"
      />
      {moveEventModalBookingId && (
        <MoveReservationContent show={showMoveEventModal} onClose={handleCloseMoveEventModal} bookingId={moveEventModalBookingId} roomTypeTargetId={moveEventModalRoomTypeId} roomNumberTargetId={moveEventModalRoomId} onCanceled={handleCancelMoveEvent} />
      )}
    </CalendarContainer>
  );
};

export default CalendarBooking;
